import React, { useEffect, useState } from 'react';

import ContentGroupsList from '../../../components/ContentGroupsList';
import DetailsModal, { Values } from '../../../components/DetailsModal';
import { useGetCoursesQuery, useNewCourseMutation } from '../../../services/courses';
import { Course } from '../../../services/courses/types';
import { GRADES } from '../../../utils/common/constants';

const INITIAL_COURSE_FORM = {
  id: '',
  grade: '',
  label: '',
  shortLabel: '',
};

interface CoursesSectionProps {
  subject?: string;
}

const CoursesSection = ({ subject }: CoursesSectionProps) => {
  /**
   * Courses Queries & Mutations
   */
  const { data: coursesData,
    isFetching: isCoursesFetching } = useGetCoursesQuery({ subject: subject || '' },
    { skip: !subject });
  const [newCourseFormOpen, setNewCourseFormOpen] = useState(false);
  const [newCourseForm, setNewCourseForm] = useState(INITIAL_COURSE_FORM);
  const [newCourse,
    {
      isSuccess: isNewCourseSuccess,
      isLoading: isNewCourseLoading,
      error: newCourseError,
      reset: resetNewCourse,
    }] = useNewCourseMutation();

  /**
   * Courses functions
   */
  const resetModal = async () => {
    await resetNewCourse();
    setNewCourseForm(INITIAL_COURSE_FORM);
  };

  const onCourseEntryCreate = (values?: Values) => {
    newCourse({
      subject: subject || '',
      attributes: values,
    });
  };

  // Close modal after course is created
  useEffect(() => {
    if (isNewCourseSuccess) {
      setNewCourseFormOpen(false);
      resetModal();
    }
  }, [isNewCourseSuccess]);

  return (
    <>
      <ContentGroupsList
        columns={[
          {
            field: 'id',
            headerName: 'Key',
            width: 120,
          },
          {
            field: 'label',
            headerName: 'Course',
            width: 360,
          },
        ]}
        data={[...(coursesData?.data || [])].sort((a, b) => a.label.localeCompare(b.label))}
        loading={isCoursesFetching}
        actions={{
          create: {
            fn: () => setNewCourseFormOpen(true),
            label: 'Add Course',
          },
        }}
        getRowHref={(row: Course) => `/admin/course/${subject}/${row?.id}`}
      />
      <DetailsModal
        open={newCourseFormOpen}
        handleClose={() => setNewCourseFormOpen(false)}
        error={newCourseError}
        title="Add New Course"
        fields={{
          id: {
            editable: true,
            type: 'text',
            label: 'Course ID',
          },
          grade: {
            editable: true,
            type: 'select',
            typeOptions: GRADES,
            label: 'Grade',
          },
          label: {
            editable: true,
            type: 'text',
          },
          shortLabel: {
            editable: true,
            type: 'text',
          },
        }}
        values={newCourseForm}
        buttons={[{
          fn: onCourseEntryCreate,
          label: 'Add',
          loading: isNewCourseLoading,
        }]}
      />
    </>
  );
};

export default CoursesSection;
